import React from 'react';

import { Box, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { ImageAvatar } from 'components/Avatar';
import { ListCard } from 'components/Card';
import { Status as StatusComponent } from 'components/Status';

import { isoToDateString } from 'utils/dateFormatter';

import { useRelatedCompaniesCount } from '../api/getRelatedCompaniesCount';
import { useCompanyFrameworkAgreementsCount } from '../api/getCompanyFrameworkAgreementsCount';
import { useCompanyContactsCount } from '../api/getCompanyContactsCount';
import { useRelatedDirectsCount } from '../api/getRelatedDirectsCount';
import { useCompanyDPSCount } from '../api/getRelatedDPSCount';
import { ReactComponent as RegisteredIcon } from 'assets/Registered.svg';

import { useAuth } from 'lib/auth';
import { paths } from 'routes';
import { useCompanyUsersCount } from '../api/getCompanyUsersCount';

export const CompaniesListItems = ({
  company_id,
  Name,
  VAT,
  Status,
  created_on,
  companyType,
  square_logo_url,
  is_shadow,
  last_user_active_at,
}) => {
  const { account } = useAuth();
  const { data: relatedCompaniesData } = useRelatedCompaniesCount(company_id, companyType);
  const relatedCompaniesCount = relatedCompaniesData?.count ?? 0;

  const { data: activeFAData } = useCompanyFrameworkAgreementsCount(company_id, { expired: false });
  const activeFACount = activeFAData?.count ?? 0;

  const { data: expiredFAData } = useCompanyFrameworkAgreementsCount(company_id, { expired: true });
  const expiredFACount = expiredFAData?.count ?? 0;

  const { data: contactsData } = useCompanyContactsCount(company_id, { includeInactive: false });
  const contactsCount = contactsData?.count ?? 0;

  const { data: usersData } = useCompanyUsersCount(company_id, { includeInactive: true });
  const usersCount = usersData?.count ?? 0;

  const { data: activeDirectsData } = useRelatedDirectsCount(company_id, { expired: false });
  const activeDirectsCount = activeDirectsData?.count ?? 0;

  const { data: expiredDirectsData } = useRelatedDirectsCount(company_id, { expired: true });
  const expiredDirectsCount = expiredDirectsData?.count ?? 0;

  const { data: activeDPSData } = useCompanyDPSCount(company_id, {
    expired: false,
    queryOptions: {
      enabled: companyType === 'buyer',
    },
  });
  const activeDPSCount = activeDPSData?.count ?? 0;

  const { data: expiredDPSData } = useCompanyDPSCount(company_id, {
    expired: true,
    queryOptions: {
      enabled: companyType === 'buyer',
    },
  });
  const expiredDPSCount = expiredDPSData?.count ?? 0;

  const tabName = companyType === 'buyer' ? 'suppliers' : 'customers';

  return (
    <ListCard>
      <ImageAvatar img={square_logo_url || ''} name={Name}/>
      <Stack flex={{ md: 3, lg: 4 }} justifyContent="center">
        <Link
          component={RouterLink}
          to={`/app/${companyType}s/${company_id.toString()}`}
          color="inherit"
        >
          <Stack direction={'row'} gap={1}>
            <Typography variant="body1" fontWeight={700}>
              {Name}
            </Typography>
            {account.super_admin && !is_shadow && (
              <RegisteredIcon width={10}/>
            )}
          </Stack>
        </Link>
        <Stack direction="row" gap={1}>
          <Typography variant="caption" fontWeight={600}>
            {VAT}
          </Typography>
        </Stack>
      </Stack>
      <Stack justifyContent="center" flex={account.super_admin ? 1.2 : 0.65} pr={{md: 1, xl: 0}}>
        <Typography variant="caption" fontWeight={700}>
          Agreements
        </Typography>
        <Link
          component={RouterLink}
          to={`/app/${companyType}s/${company_id.toString()}?tab=agreements`}
          color="inherit"
        >
          <Typography variant="body2" fontWeight={700}>
            {account.super_admin ? `${activeFACount} (${expiredFACount})` : activeFACount + expiredFACount}
          </Typography>
        </Link>
      </Stack>
      <Stack justifyContent="center" flex={account.super_admin ? 1 : 0.5} pr={{md: 1, xl: 0}}>
        <Typography variant="caption" fontWeight={700}>
          Directs
        </Typography>
        <Link
          component={RouterLink}
          to={`/app/${companyType}s/${company_id.toString()}?tab=directs`}
          color="inherit"
        >
          <Typography variant="body2" fontWeight={700}>
            {account.super_admin ? `${activeDirectsCount} (${expiredDirectsCount})` : activeDirectsCount + expiredDirectsCount}
          </Typography>
        </Link>
      </Stack>
      {companyType === 'buyer' && (
        <Stack justifyContent="center" flex={account.super_admin ? 1 : 0.5} pr={{md: 1, xl: 0}}>
          <Typography variant="caption" fontWeight={700}>
            DPS
          </Typography>
          <Link
            component={RouterLink}
            to={`/app/${companyType}s/${company_id.toString()}?tab=dps`}
            color="inherit"
          >
            <Typography variant="body2" fontWeight={700}>
              {account.super_admin ? `${activeDPSCount} (${expiredDPSCount})` : activeDPSCount + expiredDPSCount}
            </Typography>
          </Link>
        </Stack>
      )}
      <Stack justifyContent="center" flex={account.super_admin ? 1 : 0.5} pr={{md: 1, xl: 0}}>
        <Typography variant="caption" fontWeight={700}>
          {companyType === 'buyer' ? 'Suppliers' : 'Customers'}
        </Typography>
        <Link
          component={RouterLink}
          to={`/app/${companyType}s/${company_id.toString()}?tab=${tabName}`}
          color="inherit"
        >
          <Typography variant="body2" fontWeight={700}>
            {relatedCompaniesCount}
          </Typography>
        </Link>
      </Stack>
      <Stack justifyContent="center" flex={account.super_admin ? 1 : 0.4} pr={{md: 1, xl: 0}}>
        <Typography variant="caption" fontWeight={700}>
          Users
        </Typography>
        {(usersCount > 0 || account.super_admin) ? (
          <Link
            component={RouterLink}
            to={companyType === 'buyer' ? paths.buyersUsersView(company_id) : paths.suppliersUsersView(company_id)}
            color="inherit"
          >
            <Typography variant="body2" fontWeight={700}>
              {`${usersCount}`}
            </Typography>
          </Link>
        ) : (
          <Typography variant="body2" fontWeight={700}>
            {`${usersCount}`}
          </Typography>
        )}
      </Stack>
      <Stack justifyContent="center" flex={account.super_admin ? 1 : 0.4} pr={{md: 1, xl: 0}}>
        <Typography variant="caption" fontWeight={700}>
          Contacts
        </Typography>
        {contactsCount > 0 ? (
          <Link
            component={RouterLink}
            to={`/app/${companyType}s/${company_id.toString()}?tab=contacts`}
            color="inherit"
          >
            <Typography variant="body2" fontWeight={700}>
              {`${contactsCount}`}
            </Typography>
          </Link>
        ) : (
          <Typography variant="body2" fontWeight={700}>
            {`${contactsCount}`}
          </Typography>
        )}
      </Stack>
        {account.super_admin && (
        <>
          <Stack justifyContent="center" flex={1.5}>
            <Typography variant="caption" fontWeight={700} pr={{md: 1, xl: 0}}>
              Registered
            </Typography>

            <Typography variant="body2" fontWeight={700} height={1}>
              {!is_shadow ? isoToDateString(created_on, true) : null}
            </Typography>
          </Stack>

          <Stack justifyContent="center" flex={1.5}>
            <Typography variant="caption" fontWeight={700} pr={{md: 1, xl: 0}}>
              Last Active
            </Typography>

            <Typography variant="body2" fontWeight={700} height={1}>
              {last_user_active_at !== null? isoToDateString(last_user_active_at, true) : null}
            </Typography>
          </Stack>
          <StatusComponent status={Status} />
        </>
      )}
    </ListCard>
  );
};